import { useState, useCallback, useEffect, useMemo } from 'react';
import React from 'react';
// @mui
import {
  DataGridPro,
  GridColDef,
  GridToolbar,
  GridColumnVisibilityModel,
  GridLogicOperator,
  useGridApiRef,
  GridSortModel,
  GridFilterModel,
  GridPaginationModel,
  GridColType,
  GridAlignment,
} from '@mui/x-data-grid-pro';
import { Box, Stack, useTheme } from '@mui/system';
import { LinearProgress, Typography, Button, Skeleton, Tooltip } from '@mui/material';
import { fPercent } from 'src/utils/format-number';
import {
  setFilterCountry,
  setFilterDevice,
  setFilterKeyword,
  setFilterPlatform,
  setSelectedKeywords,
} from 'src/context/reducers/filter-settings/filterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useLocales } from 'src/locales';
import { useResponsive } from 'src/hooks/use-responsive';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { formatDate } from 'src/utils/dates-labels';
import { brandedCampaignsVisibility, isMetricAllowed } from 'src/utils/branded-campaigns';
import FilterPanelCustom from './filter-panel-custom/filter-panel-custom';
import DatagridDiffCell from './data-grid-diff-cell';
import "./data-grid-custom.css";
import { RootState } from 'src/context/store';
import { useKpisQuery } from 'src/context/api/data/api';
import { fDate } from 'src/utils/format-time';
import Iconify from '../iconify';
import HoverShortcut from 'src/components/visibility/components/HoverShortcut';
import CustomTooltip from 'src/components/visibility/components/CustomTooltip';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import { resolveRegionFlagCode } from 'src/utils/country-code';
import { renderMetricCell, renderBrandTrafficCell, formatComparePct, formatCompareDiff, compareColorPct, BRAND_TRAFFIC_3WAY_COLORS } from './data-grid-performance-split-business-overview';
import { compareKindForFormat } from './compare-kind';
import { columnOrderIndex } from './column-order';

export type MetricBarType = 'magnitude' | 'composition' | 'blended-composition' | 'none';
type BrandTrafficMode = 'organic-paid' | 'organic-search-shopping' | 'organic-pmax-search-shopping';

// Resolve the in-cell bar type for a metric column. When a grid opts into per-metric bars
// (`metricBarTypes` provided), a metric WITHOUT an explicit entry falls back to 'none' so it still
// renders through MetricValueCell (value + inline compare chip) instead of the bare `cell()`.
// Without that fallback, columns hidden by default in Explore (not listed in GRID_BAR_TYPES) lost
// their inline comparison once revealed. Grids that don't pass `metricBarTypes` keep `undefined`
// (legacy `cell()` rendering, unchanged).
export const resolveBarType = (
  metricBarTypes: Record<string, MetricBarType> | undefined,
  key: string,
  isDimension: boolean,
): MetricBarType | undefined => {
  if (isDimension || !metricBarTypes) return undefined;
  return metricBarTypes[key] ?? 'none';
};

// ----------------------------------------------------------------------


export enum ColumnType {
  COMPARISON = 'comparison',
  DIFF = 'diff',
  LINE = 'line',
}

const valueOrZero = (value: any) => value ?? 0;
const valueOrDate = (value: any) => value ?? fDate(new Date(), "yyyy-MM-dd");
const valueOrEmpty = (value: any) => value ?? "";

const useGetFormatter = (kpi: ReturnType<typeof useKpisQuery>["data"][number]) => {
  const { currentLang: { value: locale } } = useLocales();
  const workspace = useCurrentWorkspace();
  const [formatter, nbDecimals] = kpi?.format?.split(":") ?? ["", 0];
  const nbDecimalNumber = parseInt(nbDecimals);
  const formatterFunction = ["date", "date_day"].includes(kpi.id) ? (value: any = "") => {
    return fDate(valueOrDate(value), "yyyy-MM-dd")
  } :
    ["device", "device_type"].includes(kpi.id) ? (value: any = "") => {
      value = valueOrEmpty(value);
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    } :
      formatter === "currency" ? (value: number = 0) => {
        return Intl.NumberFormat(locale, {
          style: "currency",
          currency: workspace?.currency,
          minimumFractionDigits: nbDecimalNumber,
          maximumFractionDigits: nbDecimalNumber,
          notation: value > 1000 ? "compact" : "standard",
        }).format(valueOrZero(value));
      } : formatter === "percentage" ? (value: number = 0) => ((valueOrZero(value))?.toFixed(nbDecimalNumber) ?? 0) + "%" : ["number", "float"].includes(formatter) ? (value: number = 0) => {
        return valueOrZero(value).toLocaleString(locale, { maximumFractionDigits: nbDecimalNumber })
      } : (value: any) => value;
  return formatterFunction;
}

const ProgressCell = ({ row, kpi }: { row: any, kpi: any }) => {
  const theme = useTheme();
  const cappedValue = Math.min(row[kpi.id], 100); // Limiter Ã  100%
  return (
    <Stack spacing={1} direction="row" alignItems="center" sx={{ px: 1, width: 1, height: 1 }}>
      <LinearProgress
        value={cappedValue}
        variant="determinate"
        sx={{
          width: 1,
          height: 6,
          backgroundColor: theme.palette.mode === "dark" ? kpi["color:dark"] : kpi["color:light"],
          '& .MuiLinearProgress-bar': {
            backgroundColor: kpi["color"],
          }
        }}
      />
      <Typography variant="caption" sx={{ width: 80, fontSize: "0.825rem" }}>
        {cappedValue === 0 ? '0%' : fPercent(cappedValue)}
      </Typography>
    </Stack>
  )
}

type DiffCellProps = {
  kpi: ReturnType<typeof useKpisQuery>["data"][number];
  value: number;
  compare: number;
}

const DiffCell = ({ value, compare, kpi }: DiffCellProps) => {
  const [format, nbDecimals] = kpi.format.split(":") ?? ["", 0];

  const diff = (value - compare);
  const diffPercentage = (value - compare) / compare * 100;

  return <DatagridDiffCell
    value={parseFloat(diff.toFixed(nbDecimals))}
    compareChip={diffPercentage}
    invertedColor={kpi.invert}
    unit={format === "percentage" ? "pp" : format === "float" ? "" : "currency"}
  />

}

const InlineDifferenceCell = ({
  value,
  compare,
  kpi,
  evolutionMode = true,
  evolutionOnly = false,
}: DiffCellProps & { evolutionMode?: boolean; evolutionOnly?: boolean }) => {
  const formatterFunction = useGetFormatter(kpi);
  const diff = value - compare;

  // Some dedicated Difference columns (currently Brand Searches in Funnel) must display
  // only the evolution percentage, without repeating the raw absolute difference.
  if (evolutionOnly) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ width: 1, height: 1 }}
      >
        <Typography
          sx={{
            flexShrink: 0,
            textAlign: 'left',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
            color: compareColorPct(value, compare, kpi.invert),
          }}
        >
          {evolutionMode
            ? formatComparePct(value, compare)
            : formatCompareDiff(
                value,
                compare,
                compareKindForFormat(kpi.format)
              )}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        gap: '8px',
        width: 1,
        height: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '22px',
          color: '#333C47',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatterFunction(diff)}
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Typography
        sx={{
          flexShrink: 0,
          textAlign: 'right',
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
          color: compareColorPct(value, compare, kpi.invert),
        }}
      >
        {evolutionMode
          ? formatComparePct(value, compare)
          : formatCompareDiff(
              value,
              compare,
              compareKindForFormat(kpi.format)
            )}
      </Typography>
    </Stack>
  );
};

const BaseCell = ({ row, kpi }: { row: any; kpi: any }) => {

  const formatterFunction = useGetFormatter(kpi);

  return (
    <Typography
      variant="caption"
      sx={{
        width: 80,
        fontSize: kpi.viz === 'dimension' ? '14px' : '0.825rem',
      }}
    >
      {formatterFunction(row[kpi.id])}
    </Typography>
  );
};

// Country dimension cell: flag + name, aligned with the country selector bar
// (same circle-flags source as the header country dropdown). Opt-in via
// `showDimensionFlags` so other grids stay unchanged.
const isCountryDimension = (id: string) => ['region_name', 'country'].includes(id);

const CountryDimCell = ({ value }: { value: string }) => {
  // A region flies a flag only when the workspace maps it to a single country;
  // unmapped ones fall back to reading the label ("United States", "US").
  const workspace: any = useCurrentWorkspace();
  const code = resolveRegionFlagCode(value, workspace?.country_region_mapping);

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1,
        width: 1,
        height: 1,
      }}
    >
      {code ? (
        <FlagIcon code={code} />
      ) : value === 'EMEA' ? (
        <Iconify
          icon="emojione:globe-showing-europe-africa"
          width={16}
          height={16}
          sx={{ color: 'grey.500' }}
        />
      ) : null}

      <Typography
        variant="caption"
        sx={{
          fontSize: '14px',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

// Dimension cell with an opt-in "apply filter" hover shortcut (Explore search terms). Reuses the
// Visibility `HoverShortcut` affordance : le terme reste visible, et au survol de la cellule un
// raccourci « Filter » apparaît à droite. Le clic (dé)sélectionne la valeur dans le filtre du header.
// Quand la valeur est déjà filtrée, le raccourci reste affiché (état actif) et bascule vers « Remove ».
export const DimensionFilterCell = ({
  value,
  fieldKey,
}: {
  value: any;
  fieldKey: string;
}) => {

  const dispatch = useDispatch();
  const { t } = useLocales();
  const filterStore = useSelector((state: RootState) => state.filters);
  const workspace: any = useCurrentWorkspace();
  const [hovered, setHovered] = useState(false);

  const normalizedField = fieldKey.toLowerCase();
  const isCountry = ['region_name', 'country'].includes(normalizedField);
  const isDevice = ['device_type', 'device'].includes(normalizedField);
  const isPlatform = normalizedField === 'platform';

  const selectedValues: string[] = isCountry
    ? filterStore?.countries ?? []
    : isDevice
      ? filterStore?.devices ?? []
      : isPlatform
        ? filterStore?.platforms ?? []
        : filterStore?.keywords ?? [];

  const loadedValues: string[] = isCountry
    ? filterStore?.loadedCountries ?? []
    : isDevice
      ? filterStore?.loadedDevices ?? []
      : isPlatform
        ? filterStore?.loadedPlatform ?? []
        : [];

  const rawValue = value == null ? '' : String(value);

  // Platform rows may come back as GOOGLE/BING while the header options are Google/Bing.
  // Reuse the exact loaded option when possible so the header Autocomplete stays synchronized.
  const filterValue =
    isPlatform
      ? loadedValues.find(
          (item) => item.toLowerCase() === rawValue.toLowerCase()
        ) ?? rawValue
      : rawValue;

  const includesValue = (items: string[], candidate: string) =>
    isPlatform
      ? items.some((item) => item.toLowerCase() === candidate.toLowerCase())
      : items.includes(candidate);

  // Header semantics differ by dimension:
  // - Countries: the CountryBar stores "All" as the COMPLETE country-name list.
  //   A real country filter is therefore exactly one selected country.
  // - Devices / Platforms: the header stores "All" as an empty array.
  //
  // Result: when the CountryBar is on "All", NO country row must appear
  // individually selected ("Remove filter").
  const isAllSelected = isCountry
    ? selectedValues.length !== 1
    : (isDevice || isPlatform)
      ? selectedValues.length === 0
      : false;

  const isActive =
    filterValue !== '' &&
    !isAllSelected &&
    includesValue(selectedValues, filterValue);

  const toggle = () => {
    if (!filterValue) return;

    // From "All", clicking Filter on a row means:
    // filter down to this single Country / Device / Platform.
    if (isAllSelected && isCountry) {
      dispatch(setFilterCountry([filterValue]));
      return;
    }

    if (isAllSelected && isDevice) {
      dispatch(setFilterDevice([filterValue]));
      return;
    }

    if (isAllSelected && isPlatform) {
      dispatch(setFilterPlatform([filterValue]));
      return;
    }

    const updated = isActive
      ? selectedValues.filter((item) =>
          isPlatform
            ? item.toLowerCase() !== filterValue.toLowerCase()
            : item !== filterValue
        )
      : [...selectedValues, filterValue];

    if (isCountry) {
      // CountryBar represents "All" with the complete country list, not [].
      // When removing the only active country, restore the loaded list so
      // the top CountryBar goes back to its aggregate "All" state.
      if (isActive && updated.length === 0) {
        dispatch(setFilterCountry(loadedValues.length > 0 ? loadedValues : []));
      } else {
        dispatch(setFilterCountry(updated));
      }
      return;
    }

    if (isDevice) {
      dispatch(setFilterDevice(updated));
      return;
    }

    if (isPlatform) {
      dispatch(setFilterPlatform(updated));
      return;
    }

    // Search Term: keep the historical behavior so the header chips / selected state
    // stay synchronized exactly as before.
    dispatch(setFilterKeyword(updated));
    dispatch(setSelectedKeywords(updated));
  };

  // Raccourci au survol : label + flèche `eva:diagonal-arrow-right-up-fill`.

  const shortcut = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {t(isActive ? 'dashboard.filters.remove_filter' : 'dashboard.filters.apply_filter')}
      <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
    </Box>
  );

  // Le terme reste calé à gauche et le raccourci est poussé à droite via ml:auto.
  const displayValue = isDevice
    ? rawValue.charAt(0).toUpperCase() + rawValue.slice(1).toLowerCase()
    : isPlatform
      ? filterValue
      : rawValue;

  // Same flag rule as CountryDimCell: the workspace region mapping first, then the label.
  const flagCode = isCountry ? resolveRegionFlagCode(rawValue, workspace?.country_region_mapping) : null;


  return (
    <Stack
      direction="row"
      alignItems="center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ gap: '8px', width: 1, height: 1 }}
    >
      {isCountry && (
        flagCode ? (
          <FlagIcon code={flagCode} />
        ) : rawValue === 'EMEA' ? (
          <Iconify
            icon="emojione:globe-showing-europe-africa"
            width={16}
            height={16}
            sx={{ color: 'grey.500', flexShrink: 0 }}
          />
        ) : null
      )}

      <CustomTooltip title={displayValue} placement="top">
        <Typography
          variant="body2"
          sx={{
            fontSize: 14,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayValue}
        </Typography>
      </CustomTooltip>

      <HoverShortcut
        defaultContent=""
        hoverContent={shortcut}
        onClick={toggle}
        forceHovered={hovered || isActive}
        fontSize={14}
        sx={{ ml: 'auto', flexShrink: 0 }}
      />
    </Stack>
  );
};

// Value-only metric cell (barType 'none', e.g. Blended CPC ratio). Mirrors the value
// typography of the bar cells so the column stays visually consistent (no smaller caption font).
const MetricValueCell = React.memo(({ row, kpi, fieldKey, valueAlign = 'right', showCompare = false, evolutionMode = true }: { row: any; kpi: any; fieldKey: string; valueAlign?: 'left' | 'right'; showCompare?: boolean; evolutionMode?: boolean }) => {
  const fmt = useGetFormatter(kpi);
  const { currentLang: { value: locale } } = useLocales();
  const workspace = useCurrentWorkspace();
  const value = row[fieldKey] ?? 0;
  const compare = row.compare?.[fieldKey];
  const compareKind = compareKindForFormat(kpi.format);

  const formatAbsoluteCompare = () => {
    if (
      fieldKey === 'blended_cpc' &&
      typeof compare === 'number'
    ) {
      const diff = value - compare;

      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: workspace?.currency ?? 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(diff));

      if (diff > 0) {
        return `+${formatted}`;
      }

      if (diff < 0) {
        return `-${formatted}`;
      }

      return formatted;
    }

    return formatCompareDiff(
      value,
      compare,
      compareKind,
      {
        currency: workspace?.currency,
        locale,
      }
    );
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent={valueAlign === 'left' ? 'flex-start' : 'flex-end'}
      sx={{ gap: '8px', width: 1, height: 1 }}
    >
      <Typography
        sx={{
          textAlign: valueAlign,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '22px',
          color: '#333C47',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {fmt(value)}
      </Typography>
      {showCompare && <Box sx={{ flex: 1 }} />}
      {showCompare && (
        <Typography
          sx={{
            flexShrink: 0,
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
            color: compareColorPct(value, compare, kpi.invert),
          }}
        >
          {compareKind === 'percent'
            ? formatCompareDiff(
              value,
              compare,
              compareKind,
              { currency: workspace?.currency, locale }
            )
            : evolutionMode
              ? formatComparePct(value, compare)
              : formatAbsoluteCompare()}
        </Typography>
      )}
    </Stack>
  );
});

// Metric cell with a proportional magnitude bar (value Ã· page-max), reusing the
// tested renderer from the performance-split grid. Opt-in via `metricBarTypes`.
const MetricBarCell = React.memo(({ row, kpi, fieldKey, max, valueAlign = 'right', barColor, showCompare = false, evolutionMode = true }: { row: any; kpi: any; fieldKey: string; max: number; valueAlign?: 'left' | 'right'; barColor?: string; showCompare?: boolean; evolutionMode?: boolean }) => {
  const fmt = useGetFormatter(kpi);
  const { currentLang: { value: locale } } = useLocales();
  const workspace = useCurrentWorkspace();
  const value = row[fieldKey] ?? 0;
  const compareKind = compareKindForFormat(kpi.format);
  // Size the value slot to the column's widest value (the page-max the bar scales to) so a
  // large number never overflows onto the bar. The width is derived from `max` alone, so it's
  // identical for every row â†’ all bars stay aligned. `ch` pairs with the tabular-nums digits;
  // +1 covers narrower glyphs (commas / currency symbols). Floored at 7ch (~the old 60px).
  const valueWidth = `${Math.max(7, fmt(max).length + 1)}ch`;
  return renderMetricCell({
    displayValue: fmt(value),
    barValue: value,
    max,
    barColor: barColor ?? kpi.color,
    showCompare,
    current: value,
    compare: row.compare?.[fieldKey],
    invertedColor: kpi.invert,
    // Percent metrics must always display their evolution in percentage points (pp),
    // not as a relative percentage change.
    evolutionMode: compareKind === 'percent' ? false : evolutionMode,
    compareKind,
    currency: workspace?.currency,
    locale,
    valueWidth,
    valueAlign,
  });
});

// Brand Traffic cell: 100%-stacked organic/paid/uncaptured composition bar. Falls back to a
// plain magnitude bar when the composition fields are absent from the row (graceful degradation).
const CompositionBarCell = React.memo(({ row, kpi, fieldKey, max, valueAlign = 'right', showCompare = false, evolutionMode = true, mode = 'organic-paid' }: { row: any; kpi: any; fieldKey: string; max: number; valueAlign?: 'left' | 'right'; showCompare?: boolean; evolutionMode?: boolean; mode?: BrandTrafficMode }) => {
  const fmt = useGetFormatter(kpi);
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const value = row[fieldKey] ?? 0;

  const {
    currentLang: { value: locale },
  } = useLocales();

  if (mode === 'organic-pmax-search-shopping') {
    const split = row.brand_traffic_split ?? {};

    // Conversions - Brand Traffic:
    // toujours afficher les 4 catégories, même si une valeur vaut 0.
    const segments = [
      {
        label: 'Organic',
        value: Math.max(0, Number(split.organic) || 0),
        color: '#18A055',
      },
      {
        label: 'PMax',
        value: Math.max(0, Number(split.pmax) || 0),
        color: '#FFE299',
      },
      {
        label: 'Paid Search',
        value: Math.max(0, Number(split.paidSearch) || 0),
        color: '#FFB700',
      },
      {
        label: 'Paid Shopping',
        value: Math.max(0, Number(split.paidShopping) || 0),
        color: '#FF8C00',
      },
    ];

    const total = segments.reduce(
      (sum, segment) => sum + segment.value,
      0
    );

    const percentageOf = (segmentValue: number) =>
      total > 0 ? (segmentValue / total) * 100 : 0;

    const roundedPercentages = (() => {
      if (total <= 0) {
        return segments.map(() => 0);
      }

      const raw = segments.map((segment) =>
        percentageOf(segment.value)
      );

      const rounded = raw.map((percentage) =>
        Math.floor(percentage)
      );

      let remaining =
        100 -
        rounded.reduce(
          (sum, percentage) => sum + percentage,
          0
        );

      const order = raw
        .map((percentage, index) => ({
          index,
          remainder:
            percentage - Math.floor(percentage),
        }))
        .sort(
          (a, b) =>
            b.remainder - a.remainder
        );

      for (
        let index = 0;
        index < remaining;
        index += 1
      ) {
        rounded[
          order[index % order.length].index
        ] += 1;
      }

      return rounded;
    })();

    const tooltip = (
      <Stack
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          px: '12px',
          py: '12px',
        }}
      >
        <Typography
          sx={{
            color: '#333C47',
            fontFamily:
              'Public Sans, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '100%',
            mb: '12px',
          }}
        >
          Brand Traffic
        </Typography>

        {segments.map(
          (segment, index) => (
            <Stack
              key={segment.label}
              direction="row"
              alignItems="center"
              sx={{
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                p: '4px',
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  width: '4px',
                  height: '20px',
                  borderRadius: '2px',
                  bgcolor:
                    segment.color,
                  flexShrink: 0,
                }}
              />

              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: '#333C47',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                }}
              >
                {segment.label}
              </Typography>

              <Typography
                sx={{
                  width: '40px',
                  flexShrink: 0,
                  textAlign: 'right',
                  color: '#637381',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${roundedPercentages[index]}%`}
              </Typography>
            </Stack>
          )
        )}
      </Stack>
    );

    return (
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          gap: '8px',
          width: 1,
          height: 1,
        }}
      >
        <Typography
          sx={{
            width: 70,
            flexShrink: 0,
            textAlign: valueAlign,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '22px',
            color: '#333C47',
            whiteSpace: 'nowrap',
            fontVariantNumeric:
              'tabular-nums',
          }}
        >
          {fmt(value)}
        </Typography>

        <Tooltip
          title={tooltip}
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                width: '320px',
                minWidth: '320px',
                maxWidth: '320px',
                boxSizing:
                  'border-box',
                p: 0,
              },
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 8,
              height: 6,
              borderRadius: '6px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'stretch',
              bgcolor:
                total > 0
                  ? 'transparent'
                  : 'rgba(145, 158, 171, 0.20)',
            }}
          >
            {segments
              .filter((segment) => segment.value > 0)
              .map((segment) => {
                const percentage =
                  percentageOf(
                    segment.value
                  );

                return (
                  <Box
                    key={segment.label}
                    sx={{
                      width: `${percentage}%`,
                      height: '100%',
                      bgcolor: segment.color,
                      flexShrink: 0,
                    }}
                  />
                );
              })}
          </Box>
        </Tooltip>

        {showCompare && (
          <Typography
            sx={{
              width: 56,
              flexShrink: 0,
              textAlign: 'right',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              fontVariantNumeric:
                'tabular-nums',
              color: compareColorPct(
                value,
                row.compare?.[
                fieldKey
                ],
                kpi.invert
              ),
            }}
          >
            {evolutionMode
              ? formatComparePct(
                value,
                row.compare?.[
                fieldKey
                ]
              )
              : formatCompareDiff(
                value,
                row.compare?.[
                fieldKey
                ],
                'number',
                {
                  currency:
                    workspace?.currency,
                  locale,
                }
              )}
          </Typography>
        )}
      </Stack>
    );
  }

  // DÃ©coupage 3-way (Explore) : Organic / Paid Search / Paid Shopping (shopping + pmax regroupÃ©s).
  // Fallback gracieux vers le 2-way ci-dessous si les champs 3-way sont absents de la ligne.
  if (mode === 'organic-search-shopping') {
    // Le back (traffic table.sql) expose les clics sous les alias *_brand_traffic ; on garde les
    // noms bruts ad_clicks_from_* en fallback. shopping_brand_traffic vaut -1 quand is_shopping=FALSE
    // (template lead sans shoppingIncluded) => clampÃ© Ã  0 par Math.max.
    // Shopping / PMax ne contribuent que si leurs campagnes brandÃ©es sont configurÃ©es
    // dans le workspace ; sans aucune des deux, le segment Paid Shopping disparaÃ®t.
    const { showShopping, showPmax } = brandedCampaignsVisibility(workspace);
    const organic3 = row.organic_clicks ?? row.organic_brand_traffic;
    const paidSearch = row.ad_clicks_from_search ?? row.search_brand_traffic;
    const shopping = showShopping ? row.ad_clicks_from_shopping ?? row.shopping_brand_traffic ?? 0 : 0;
    const pmax = showPmax ? row.ad_clicks_from_pmax ?? row.pmax_brand_traffic ?? 0 : 0;
    const paidShopping = Math.max(0, shopping) + Math.max(0, pmax);
    const total3 =
      Math.max(0, organic3 ?? 0) + Math.max(0, paidSearch ?? 0) + Math.max(0, paidShopping ?? 0);
    if (total3 > 0) {
      return renderBrandTrafficCell({
        displayValue: fmt(value),
        showCompare,
        current: value,
        compare: row.compare?.[fieldKey],
        evolutionMode,
        t,
        valueAlign,
        segments: [
          { label: 'Organic Traffic', value: organic3, color: BRAND_TRAFFIC_3WAY_COLORS.organic },
          { label: 'Paid Search Traffic', value: paidSearch, color: BRAND_TRAFFIC_3WAY_COLORS.paidSearch },
          // Segment groupÃ© shopping + pmax : Ã©tiquette PMax quand seul PMax est configurÃ©.
          ...(showShopping || showPmax
            ? [{ label: showShopping ? 'Paid Shopping Traffic' : 'Paid PMax Traffic', value: paidShopping, color: BRAND_TRAFFIC_3WAY_COLORS.paidShopping }]
            : []),
        ],
        compareKind:
          fieldKey === 'blended_revenue'
            ? 'currency'
            : 'number',

        currency:
          workspace?.currency,

        locale,
      });
    }
  }

  const organic = row.organic_brand_traffic;
  const paid = row.paid_brand_traffic ?? row.ad_brand_traffic;
  const uncaptured = row.uncaptured_brand_traffic;
  const hasComposition =
    Math.max(0, organic ?? 0) + Math.max(0, paid ?? 0) + Math.max(0, uncaptured ?? 0) > 0;
  if (!hasComposition) {
    return (
      <MetricBarCell row={row} kpi={kpi} fieldKey={fieldKey} max={max} valueAlign={valueAlign} showCompare={showCompare} evolutionMode={evolutionMode} />
    );
  }
  return renderBrandTrafficCell({
    displayValue: fmt(value),
    organic,
    paid,
    uncaptured,
    showCompare,
    current: value,
    compare: row.compare?.[fieldKey],
    evolutionMode,
    t,
    valueAlign,
  });
});

type BlendedConversionsSegment = {
  label: string;
  value: number;
  color: string;
};

// Dedicated renderer for Blended Conversions.
// Kept separate from renderBrandTrafficCell so Brand Traffic keeps its own tooltip/behavior.
const renderBlendedConversionsCell = ({
  displayValue,
  current,
  compare,
  showCompare,
  evolutionMode = true,
  valueAlign = 'right',
  segments,
}: {
  displayValue: React.ReactNode;
  current: number;
  compare?: number;
  showCompare?: boolean;
  evolutionMode?: boolean;
  valueAlign?: 'left' | 'right';
  segments: BlendedConversionsSegment[];
}) => {
  const parts = segments.filter((segment) => Math.max(0, Number(segment.value) || 0) > 0);
  const total = parts.reduce((sum, part) => sum + Math.max(0, Number(part.value) || 0), 0);
  const hasData = total > 0;
  const pctOf = (value: number) =>
    total > 0 ? (Math.max(0, Number(value) || 0) / total) * 100 : 0;

  const rawPercentages = parts.map((part) => pctOf(part.value));
  const roundedPercentages = rawPercentages.map((percentage) => Math.floor(percentage));
  const remainingPercentage =
    100 - roundedPercentages.reduce((sum, percentage) => sum + percentage, 0);

  const percentageOrder = rawPercentages
    .map((percentage, index) => ({
      index,
      remainder: percentage - Math.floor(percentage),
    }))
    .sort((a, b) => b.remainder - a.remainder);

  for (let i = 0; i < remainingPercentage && percentageOrder[i]; i += 1) {
    roundedPercentages[percentageOrder[i].index] += 1;
  }

  const tooltip = (
    <Stack
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: '12px',
        py: '12px',
      }}
    >
      <Typography
        sx={{
          color: '#333C47',
          fontFamily: 'Public Sans, sans-serif',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '100%',
          mb: '12px',
        }}
      >
        Blended Conversions
      </Typography>

      {parts.map((segment, index) => (
        <Stack
          key={segment.label}
          direction="row"
          alignItems="center"
          sx={{
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            p: '4px',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              width: '4px',
              height: '20px',
              borderRadius: '2px',
              bgcolor: segment.color,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              color: '#333C47',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            }}
          >
            {segment.label}
          </Typography>
          <Typography
            sx={{
              width: '40px',
              flexShrink: 0,
              textAlign: 'right',
              color: '#637381',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            }}
          >
            {`${roundedPercentages[index]}%`}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack direction="row" alignItems="center" sx={{ gap: '8px', width: 1, height: 1 }}>
      <Typography
        sx={{
          width: 70,
          flexShrink: 0,
          textAlign: valueAlign,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '22px',
          color: '#333C47',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {displayValue}
      </Typography>
      <Tooltip
        title={tooltip}
        arrow
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              width: '320px',
              minWidth: '320px',
              maxWidth: '320px',
              boxSizing: 'border-box',
              p: 0,
            },
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 8,
            height: 6,
            borderRadius: '6px',
            overflow: 'hidden',
            display: 'flex',
            bgcolor: hasData ? 'transparent' : 'rgba(145, 158, 171, 0.20)',
          }}
        >
          {hasData &&
            parts.map((segment) => (
              <Box
                key={segment.label}
                sx={{
                  width: `${pctOf(segment.value)}%`,
                  bgcolor: segment.color,
                }}
              />
            ))}
        </Box>
      </Tooltip>
      {showCompare && (
        <Typography
          sx={{
            width: 56,
            flexShrink: 0,
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
            color: compareColorPct(current, compare, false),
          }}
        >
          {evolutionMode
            ? formatComparePct(current, compare)
            : formatCompareDiff(current, compare, 'number')}
        </Typography>
      )}
    </Stack>
  );
};

type BlendedRevenueSegment = {
  label: string;
  value: number;
  color: string;
};

// Dedicated renderer for Blended Revenue.
// Revenue always uses the *_revenue fields and keeps its tooltip/compare formatting separate
// from both Brand Traffic and Blended Conversions.
const renderBlendedRevenueCell = ({
  displayValue,
  current,
  compare,
  showCompare,
  evolutionMode = true,
  valueAlign = 'right',
  segments,
  currency,
  locale,
}: {
  displayValue: React.ReactNode;
  current: number;
  compare?: number;
  showCompare?: boolean;
  evolutionMode?: boolean;
  valueAlign?: 'left' | 'right';
  segments: BlendedRevenueSegment[];
  currency?: string;
  locale?: string;
}) => {
  const parts = segments.filter((segment) => Math.max(0, Number(segment.value) || 0) > 0);
  const total = parts.reduce((sum, part) => sum + Math.max(0, Number(part.value) || 0), 0);
  const hasData = total > 0;
  const pctOf = (value: number) =>
    total > 0 ? (Math.max(0, Number(value) || 0) / total) * 100 : 0;

  const rawPercentages = parts.map((part) => pctOf(part.value));
  const roundedPercentages = rawPercentages.map((percentage) => Math.floor(percentage));
  const remainingPercentage =
    100 - roundedPercentages.reduce((sum, percentage) => sum + percentage, 0);

  const percentageOrder = rawPercentages
    .map((percentage, index) => ({
      index,
      remainder: percentage - Math.floor(percentage),
    }))
    .sort((a, b) => b.remainder - a.remainder);

  for (let i = 0; i < remainingPercentage && percentageOrder[i]; i += 1) {
    roundedPercentages[percentageOrder[i].index] += 1;
  }

  const tooltip = (
    <Stack
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: '12px',
        py: '12px',
      }}
    >
      <Typography
        sx={{
          color: '#333C47',
          fontFamily: 'Public Sans, sans-serif',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '100%',
          mb: '12px',
        }}
      >
        Blended Revenue
      </Typography>

      {parts.map((segment, index) => (
        <Stack
          key={segment.label}
          direction="row"
          alignItems="center"
          sx={{
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            p: '4px',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              width: '4px',
              height: '20px',
              borderRadius: '2px',
              bgcolor: segment.color,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              color: '#333C47',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            }}
          >
            {segment.label}
          </Typography>
          <Typography
            sx={{
              width: '40px',
              flexShrink: 0,
              textAlign: 'right',
              color: '#637381',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            }}
          >
            {`${roundedPercentages[index]}%`}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack direction="row" alignItems="center" sx={{ gap: '8px', width: 1, height: 1 }}>
      <Typography
        sx={{
          width: 70,
          flexShrink: 0,
          textAlign: valueAlign,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '22px',
          color: '#333C47',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {displayValue}
      </Typography>
      <Tooltip
        title={tooltip}
        arrow
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              width: '320px',
              minWidth: '320px',
              maxWidth: '320px',
              boxSizing: 'border-box',
              p: 0,
            },
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 8,
            height: 6,
            borderRadius: '6px',
            overflow: 'hidden',
            display: 'flex',
            bgcolor: hasData ? 'transparent' : 'rgba(145, 158, 171, 0.20)',
          }}
        >
          {hasData &&
            parts.map((segment) => (
              <Box
                key={segment.label}
                sx={{
                  width: `${pctOf(segment.value)}%`,
                  bgcolor: segment.color,
                }}
              />
            ))}
        </Box>
      </Tooltip>
      {showCompare && (
        <Typography
          sx={{
            width: 56,
            flexShrink: 0,
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
            color: compareColorPct(current, compare, false),
          }}
        >
          {evolutionMode
            ? formatComparePct(current, compare)
            : formatCompareDiff(current, compare, 'currency', { currency, locale })}
        </Typography>
      )}
    </Stack>
  );
};

// Cellules mÃ©moÃ¯sÃ©es : lors d'un re-render de la grille (ex. toggle d'une colonne dans le panneau,
// tri, pagination), les props par cellule (row/kpi refs stables + primitives) sont inchangÃ©es, donc
// React.memo court-circuite le re-render de la centaine de cellules et de leurs hooks.
const BlendedCompositionBarCell = React.memo(({ row, kpi, fieldKey, max, valueAlign = 'right', showCompare = false, evolutionMode = true }: { row: any; kpi: any; fieldKey: string; max: number; valueAlign?: 'left' | 'right'; showCompare?: boolean; evolutionMode?: boolean }) => {
  const fmt = useGetFormatter(kpi);

  const {
    currentLang: { value: locale },
  } = useLocales();

  const workspace = useCurrentWorkspace();
  const value = row[fieldKey] ?? 0;
  const { showShopping, showPmax } = brandedCampaignsVisibility(workspace);

  const isRevenue = fieldKey === 'blended_revenue';

  const organic = Number(
    isRevenue ? row.organic_revenue : row.organic_conversions
  ) || 0;

  const detailedPaidValue = isRevenue
    ? row.search_paid_revenue
    : row.search_paid_conversions;

  const hasDetailedPaid = detailedPaidValue !== undefined && detailedPaidValue !== null;

  // Custom model: Organic + aggregate Paid.
  if (!hasDetailedPaid) {
    const paid = Number(
      isRevenue ? row.paid_revenue : row.paid_conversions
    ) || 0;

    const total = Math.max(0, organic) + Math.max(0, paid);

    if (total > 0) {
      if (!isRevenue) {
        return renderBlendedConversionsCell({
          displayValue: fmt(value),
          showCompare,
          current: value,
          compare: row.compare?.[fieldKey],
          evolutionMode,
          valueAlign,
          segments: [
            { label: 'Organic', value: organic, color: '#18A055' },
            { label: 'Paid', value: paid, color: '#FFB700' },
          ],
        });
      }

      return renderBlendedRevenueCell({
        displayValue: fmt(value),
        showCompare,
        current: value,
        compare: row.compare?.[fieldKey],
        evolutionMode,
        valueAlign,
        segments: [
          { label: 'Organic', value: organic, color: '#18A055' },
          { label: 'Paid', value: paid, color: '#FFB700' },
        ],
        currency: workspace?.currency,
        locale,
      });
    }

    return (
      <MetricBarCell
        row={row}
        kpi={kpi}
        fieldKey={fieldKey}
        max={max}
        valueAlign={valueAlign}
        showCompare={showCompare}
        evolutionMode={evolutionMode}
      />
    );
  }

  // Standard model: Organic + Search + optional Shopping + optional PMax.
  const searchPaid = Number(detailedPaidValue) || 0;
  const shoppingPaid = showShopping
    ? Number(isRevenue ? row.shopping_paid_revenue : row.shopping_paid_conversions) || 0
    : 0;
  const pmaxPaid = showPmax
    ? Number(isRevenue ? row.pmax_paid_revenue : row.pmax_paid_conversions) || 0
    : 0;

  const total =
    Math.max(0, organic) +
    Math.max(0, searchPaid) +
    Math.max(0, shoppingPaid) +
    Math.max(0, pmaxPaid);

  if (total > 0) {
    const segments = [
      { label: 'Organic', value: organic, color: '#18A055' },
      { label: 'Paid Search', value: searchPaid, color: '#FFB700' },
      ...(showShopping
        ? [{ label: 'Paid Shopping', value: shoppingPaid, color: '#FF8C00' }]
        : []),
      ...(showPmax
        ? [{ label: 'PMax', value: pmaxPaid, color: '#FFE299' }]
        : []),
    ];

    if (!isRevenue) {
      return renderBlendedConversionsCell({
        displayValue: fmt(value),
        showCompare,
        current: value,
        compare: row.compare?.[fieldKey],
        evolutionMode,
        valueAlign,
        segments,
      });
    }

    return renderBlendedRevenueCell({
      displayValue: fmt(value),
      showCompare,
      current: value,
      compare: row.compare?.[fieldKey],
      evolutionMode,
      valueAlign,
      segments,
      currency: workspace?.currency,
      locale,
    });
  }

  return (
    <MetricBarCell
      row={row}
      kpi={kpi}
      fieldKey={fieldKey}
      max={max}
      valueAlign={valueAlign}
      showCompare={showCompare}
      evolutionMode={evolutionMode}
    />
  );
});

MetricValueCell.displayName = 'MetricValueCell';
MetricBarCell.displayName = 'MetricBarCell';
CompositionBarCell.displayName = 'CompositionBarCell';
BlendedCompositionBarCell.displayName = 'BlendedCompositionBarCell';

const useMakeCell = (showDimensionFlags?: boolean, metricBarTypes?: Record<string, MetricBarType>, pageMax?: Record<string, number>, leftAlign?: boolean, metricBarColors?: Record<string, string>, inlineCompare?: boolean, evolutionMode?: boolean, columnLabelOverrides?: Record<string, string>, brandTrafficMode?: BrandTrafficMode, dimensionFilterField?: string, columnMinWidths?: Record<string, number>, separateDiffFields?: string[]) => {
  const { currentLang: { value: locale } } = useLocales();
  const filterStore = useSelector((state: RootState) => state.filters);
  const lgUp = useResponsive('up', 'lg');
  const kpis = useKpisQuery().data ?? [];

  return (row: any, kpi: any, key: string) => {
    const compareEnabled = filterStore?.compare;
    const columns: (GridColDef & { hide?: boolean })[] = [];

    // LibellÃ© d'en-tÃªte : override par champ (opt-in) sinon titre du catalogue KPI.
    const label = columnLabelOverrides?.[key] ?? (kpi[locale as keyof typeof kpis[0]] as string);

    const [type, align]: [GridColType, GridAlignment] =
      kpi.viz === "dimension" ? ["string", "left"] : ["number", "right"];

    const Progress =
      kpi.format.split(":").includes("percentage") &&
        ["date", "date_day", "year_week", "year_month"].some((item) =>
          Object.keys(row).includes(item)
        )
        ? ProgressCell
        : BaseCell;

    const cell = (row: any) => <Progress row={row} kpi={kpi} />;

    const headerTextAlign = leftAlign ? "left" : "right";
    const HeaderCell = (suffix: string = "", compare: boolean = false) => {
      const headerLabel = `${label}${suffix ? ` ${suffix}` : ""}`;

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: leftAlign ? "flex-start" : "flex-end",
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <span
            title={headerLabel}
            style={{
              display: "block",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: headerTextAlign,
            }}
          >
            {headerLabel}
          </span>

          {compareEnabled && suffix === "Compare" && (
            <span
              style={{
                display: "block",
                width: "100%",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: headerTextAlign,
              }}
            >
              {formatDate(compare ? filterStore?.compareStart : filterStore?.start, locale)} -{" "}
              {formatDate(compare ? filterStore?.compareEnd : filterStore?.end, locale)}
            </span>
          )}
        </div>
      );
    };

    // No-bar metric cells (e.g. Blended CPC ratio) render only a value, so they need
    // less horizontal room than bar cells. Opt-in via `metricBarTypes[key] === 'none'`.
    const isNoBarMetric = kpi.viz !== "dimension" && metricBarTypes?.[key] === "none";
    const [defaultMinWidth, defaultMaxWidth] =
      (isNoBarMetric ? [120, 160] : [190, 500]) as [number, number];

    const isBlendedCpc = key === 'blended_cpc';

    const minWidth =
      columnMinWidths?.[key] ??
      (isBlendedCpc
        ? defaultMinWidth + 20
        : defaultMinWidth);

    const maxWidth =
      Math.max(
        minWidth,
        isBlendedCpc
          ? defaultMaxWidth + 20
          : defaultMaxWidth
      );

    const valueGetter =
      ["date", "date_day"].includes(key)
        ? (_: any, row: any) =>
          row?.[key]
            ? formatDate(row[key], locale)
            : ""
        : (_: any, row: any) =>
          row?.[key];

    const isDate = ["date", "date_day", "year_week", "year_month"].includes(key);
    // Some call sites may opt a metric out of inline comparison so its Difference can live in its
    // own independently sortable column (e.g. Brand Searches in Funnel/Explore).
    const hasSeparateDiff = !!separateDiffFields?.includes(key);

    // Inline date-comparison indicator inside each metric cell (Overview style).
    // It stays enabled even when the metric also has a dedicated Difference column
    // (e.g. Brand Searches in Explore).
    const showInlineCompare = !!(
      inlineCompare &&
      compareEnabled &&
      kpi.viz !== "dimension" &&
      !isDate
    );

    // âœ… Colonne principale
    columns.push({
      field: key,
      hide: kpi.viz !== "dimension" ? !kpi.show : false,
      align: leftAlign ? "left" : align,
      headerAlign: leftAlign ? "left" : undefined,
      type,
      headerName: label,
      minWidth,
      maxWidth,
      flex: lgUp ? 1 : undefined,
      renderHeader: () => HeaderCell(),
      renderCell: (params: any) => {
        // Dimension avec raccourci « apply filter » au survol.
        // Explore l'active pour Search Terms, Countries et Devices.
        if (kpi.viz === "dimension" && dimensionFilterField && key === dimensionFilterField) {
          return (
            <DimensionFilterCell
              value={params.row[key]}
              fieldKey={key}
            />
          );
        }

        if (showDimensionFlags && kpi.viz === "dimension" && isCountryDimension(kpi.id)) {
          return <CountryDimCell value={params.row[key]} />;
        }

        const barType = resolveBarType(metricBarTypes, key, kpi.viz === "dimension");
        if (barType === "magnitude") {
          return <MetricBarCell row={params.row} kpi={kpi} fieldKey={key} max={pageMax?.[key] ?? 0} valueAlign={leftAlign ? "left" : "right"} barColor={metricBarColors?.[key]} showCompare={showInlineCompare} evolutionMode={evolutionMode} />;
        }
        if (barType === "composition") {
          return <CompositionBarCell row={params.row} kpi={kpi} fieldKey={key} max={pageMax?.[key] ?? 0} valueAlign={leftAlign ? "left" : "right"} showCompare={showInlineCompare} evolutionMode={evolutionMode} mode={brandTrafficMode} />;
        }
        if (barType === "blended-composition") {
          return <BlendedCompositionBarCell row={params.row} kpi={kpi} fieldKey={key} max={pageMax?.[key] ?? 0} valueAlign={leftAlign ? "left" : "right"} showCompare={showInlineCompare} evolutionMode={evolutionMode} />;
        }
        if (barType === "none") {
          return <MetricValueCell row={params.row} kpi={kpi} fieldKey={key} valueAlign={leftAlign ? "left" : "right"} showCompare={showInlineCompare} evolutionMode={evolutionMode} />;
        }
        return cell(params.row);
      },
      valueGetter,
    });

    // Legacy Compare column: kept only when comparisons are not rendered inline.
    if (compareEnabled && kpi.viz !== "dimension" && !isDate && !inlineCompare) {
      columns.push({
        field: `${key}Compare`,
        hide: !kpi.show,
        align: leftAlign ? "left" : align,
        headerAlign: leftAlign ? "left" : undefined,
        type,
        headerName: `${label} Compare`,
        minWidth,
        maxWidth,
        flex: lgUp ? 1 : undefined,
        renderHeader: () => HeaderCell("Compare", true),
        renderCell: (params: any) =>
          cell(params.row?.compare ?? {}),

        valueGetter: (_, row: any) =>
          row?.compare?.[key] ?? 0,
      });

    }

    // Difference column: historical behavior when inline comparison is disabled,
    // plus the per-field opt-in supplied through `separateDiffFields`.
    const showSeparateDiff = !!(
      compareEnabled &&
      kpi.viz !== "dimension" &&
      !isDate &&
      (!inlineCompare || hasSeparateDiff)
    );

    if (showSeparateDiff) {
      const isBrandSearchesDiff = key === 'brand_searches';

      columns.push({
        field: `${key}Diff`,
        hide: !kpi.show,
        align: leftAlign ? "left" : align,
        headerAlign: leftAlign ? "left" : undefined,
        type,
        headerName: isBrandSearchesDiff ? 'Br S. Diff' : `${label} Difference`,
        minWidth: isBrandSearchesDiff ? 100 : 225,
        width: isBrandSearchesDiff ? 100 : 225,
        maxWidth: isBrandSearchesDiff ? 100 : 350,
        flex: isBrandSearchesDiff ? undefined : lgUp ? 1 : undefined,
        renderHeader: () =>
          isBrandSearchesDiff ? (
            <div style={{ whiteSpace: 'nowrap' }}>Br S. Diff</div>
          ) : (
            HeaderCell("Difference")
          ),
        renderCell: (params: any) =>
          inlineCompare && hasSeparateDiff ? (
            <InlineDifferenceCell
              value={params.row?.[key] ?? 0}
              compare={params.row?.compare?.[key] ?? 0}
              kpi={kpi}
              evolutionMode={evolutionMode}
              evolutionOnly={isBrandSearchesDiff}
            />
          ) : (
            <DiffCell
              value={params.row?.[key] ?? 0}
              compare={params.row?.compare?.[key] ?? 0}
              kpi={kpi}
            />
          ),
        valueGetter: (_, row: any) => row.diff?.[key] ?? 0,

      });
    }

    return columns;
  };
};


const useCreateColumns = <T extends GridColDef>(data: any, excludeColumns: string[] = [], showDimensionFlags?: boolean, metricBarTypes?: Record<string, MetricBarType>, leftAlign?: boolean, metricBarColors?: Record<string, string>, inlineCompare?: boolean, evolutionMode?: boolean, columnOrder?: string[], columnLabelOverrides?: Record<string, string>, brandTrafficMode?: BrandTrafficMode, dimensionFilterField?: string, columnMinWidths?: Record<string, number>, kpiOverrides?: Record<string, any>, separateDiffFields?: string[]): T[] => {
  const { data: kpis = [] } = useKpisQuery();
  const { currentLang: { value: locale } } = useLocales();
  const lgUp = useResponsive('up', 'lg');
  const filterStore = useSelector((state: RootState) => state.filters);
  const workspace = useCurrentWorkspace();

  // Page-relative max per bar'd metric column (magnitude/composition bars scale to the visible page).
  const pageMax = useMemo(() => {
    const maxes: Record<string, number> = {};
    if (metricBarTypes && Array.isArray(data) && data.length) {
      Object.keys(metricBarTypes).forEach((k) => {
        if (metricBarTypes[k] !== 'none') {
          maxes[k] = Math.max(0, ...data.map((r: any) => Number(r?.[k]) || 0));
        }
      });
    }
    return maxes;
  }, [data, metricBarTypes]);

  const makeCell = useMakeCell(showDimensionFlags, metricBarTypes, pageMax, leftAlign, metricBarColors, inlineCompare, evolutionMode, columnLabelOverrides, brandTrafficMode, dimensionFilterField, columnMinWidths, separateDiffFields);

  // Colonnes Shopping / PMax exclues quand les campagnes brandÃ©es correspondantes
  // ne sont pas configurÃ©es dans le workspace (elles n'ont alors aucune donnÃ©e).
  const brandedVisibility = brandedCampaignsVisibility(workspace);

  const definedColumns: T[] = useMemo(() => {
    const make_row = (row: any, acc: GridColDef[]) => {
      Object.keys(row).forEach((key) => {
        const kpi = kpiOverrides?.[key] ?? kpis.find((kpi) => kpi.id === key);
        const activation = filterStore?.extraOptions?.mode === "activations";
        const kpiViz = (
          kpi?.viz === "both"
          || (activation && kpi?.viz === "activation")
          || (!activation && kpi?.viz === "base")
        );

        if (
          (kpi?.viz === "dimension" || kpiViz) && row[key] !== -1
          && isMetricAllowed(key, brandedVisibility)) {
          const columns: GridColDef[] = makeCell(row, kpi, key);

          columns.forEach((column, i) => {
            const index = acc.findIndex((col: GridColDef) => col.field === column.field);

            if (index !== -1) {
              // Remplacer la colonne existante
              acc[index] = column;
            } else {
              acc.push(column);
            }
          });
        }
      });
      return acc;
    };

    return data.reduce((acc: GridColDef[], row: any) => make_row(row, acc), [] as GridColDef[]).sort((a: GridColDef, b: GridColDef) => {
      // Explicit maquette order (columnOrder) takes precedence; fields not listed there fall back
      // to the KPI catalog `order` and are placed after the explicitly ordered ones.
      const aIndex = columnOrderIndex(a.field, columnOrder);
      const bIndex = columnOrderIndex(b.field, columnOrder);
      if (aIndex !== -1 || bIndex !== -1) {
        const aRank = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const bRank = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (aRank !== bRank) return aRank - bRank;
      }
      const aBaseField = a.field.replace("Compare", "").replace("Diff", "");
      const bBaseField = b.field.replace("Compare", "").replace("Diff", "");
      const aPosition = (kpiOverrides?.[aBaseField] ?? kpis.find((kpi) => kpi.id === aBaseField))?.order ?? 0;
      const bPosition = (kpiOverrides?.[bBaseField] ?? kpis.find((kpi) => kpi.id === bBaseField))?.order ?? 0;
      return aPosition - bPosition;
    }) as unknown as T[];
  }, [
    workspace,
    kpis,
    kpiOverrides,
    filterStore?.compare,
    lgUp,
    locale,
    data,
    showDimensionFlags,
    metricBarTypes,
    leftAlign,
    metricBarColors,
    inlineCompare,
    evolutionMode,
    columnOrder,
    dimensionFilterField,
    columnMinWidths,
    separateDiffFields,
  ]);



  return definedColumns;
};

// ----------------------------------------------------------------------

type Props = {
  data: {
    id: string;
    keyword: string;
    brandSearches?: number; // Add optional brandSearches property
    [key: string]: any; // Allow for other properties that might be used
  }[];
  paginationModel: any;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  paginationMode?: 'server';
  filterMode?: 'server' | 'client';
  sortMode?: 'server';
  /** Sorting strategy forwarded to DataGridPro. Defaults to server to preserve existing grids. */
  sortingMode?: 'server' | 'client';
  rowCount?: number;
  /** Affiche l'overlay de chargement natif du grid (refetch d'arriÃ¨re-plan) sans dÃ©monter la grille.
      Additif : omis â†’ comportement inchangÃ©. */
  loading?: boolean;
  /** Disable the internal columns-ready skeleton. Useful when the parent already owns the loading skeleton. */
  disableColumnsLoadingSkeleton?: boolean;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
  onSortModelChange?: (model: GridSortModel) => void;
  onFilterModelChange?: (newModel: GridFilterModel) => void;
  pinnedColumns?: any[];
  onExport?: (columns: GridColDef[]) => void;
  visibilityModel?: GridColumnVisibilityModel;
  onVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  /** Optional external grid apiRef. Defaults to an internal ref (existing call sites unchanged). */
  apiRef?: any;
  /** Hide the built-in GridToolbar (e.g. when the parent renders its own icon toolbar). Default false. */
  hideToolbar?: boolean;
  /** Base KPI fields visible by default; all other metric columns start hidden. Dimension/pinned
   *  columns always stay visible. Omit to keep the existing per-KPI `show` behavior. */
  defaultVisibleFields?: string[];
  /** Render a fixed-height grid with internal vertical scroll (like Competitors). Omit â†’ autoHeight. */
  gridHeight?: number | string;
  /** Show a country flag next to country dimension values, aligned with the country bar. Default false. */
  showDimensionFlags?: boolean;
  /** Per-metric in-cell bar: 'magnitude' (value Ã· page-max), 'composition' (brand-traffic stacked),
   *  or 'none'. Keys are KPI field ids. Omit â†’ no bars (existing behavior). */
  metricBarTypes?: Record<string, MetricBarType>;
  /** Row height forwarded to DataGridPro. Omit â†’ MUI default (existing behavior). Prefer this over
   *  CSS height overrides so the virtualizer geometry stays correct (otherwise the first row clips). */
  rowHeight?: number;
  /** Column header height forwarded to DataGridPro. Omit â†’ MUI default (existing behavior). */
  columnHeaderHeight?: number;
  /** Left-align the whole grid: column header labels AND cell values (incl. metric/numeric
   *  columns and in-cell bar values). Omit â†’ existing behavior (metrics right-aligned). */
  leftAlign?: boolean;
  /** Per-metric override for the magnitude bar color (keys are KPI field ids). Omit a field â†’
   *  falls back to `kpi.color`. Only affects 'magnitude' bars (composition uses its own palette). */
  metricBarColors?: Record<string, string>;
  /** Inline date-comparison indicator inside each metric cell (Overview style) instead of the
   *  separate Compare/Diff columns. Only effective when a date comparison is active. */
  inlineCompare?: boolean;
  /** When inlineCompare is on: true â†’ show % evolution, false â†’ absolute difference. Default true. */
  evolutionMode?: boolean;
  /** Explicit left-to-right column order (KPI field ids). Listed fields are ordered first in this
   *  sequence; unlisted metric columns fall back to the KPI catalog `order`. Omit â†’ catalog order. */
  columnOrder?: string[];
  /** Anchor element for the columns/filter panel popover. Set this to the clicked toolbar button's
   *  DOM node when the call site renders its own header buttons outside the grid (e.g. Explore with
   *  `hideToolbar`), so the panel opens under the button. Omit â†’ MUI default anchoring (unchanged). */
  panelAnchorEl?: HTMLElement | null;
  /** Per-field column header label overrides (KPI field id â†’ label). Omit a field â†’ KPI catalog
   *  title (existing behavior). Lets a call site rename a column header without touching the catalog. */
  columnLabelOverrides?: Record<string, string>;
  /** Brand-traffic composition split for 'composition' bars. 'organic-paid' (default, existing 2-way)
   *  or 'organic-search-shopping' (Explore 3-way: organic_clicks / ad_clicks_from_search /
   *  ad_clicks_from_shopping+pmax). Falls back to 2-way if the 3-way fields are absent from the row. */
  brandTrafficMode?: BrandTrafficMode;
  /** Preserve the user's column show/hide choices across data refetches & column rebuilds
   *  (filter, sort, evolution toggleâ€¦). Default false â†’ historical reset-to-defaults behavior. */
  persistColumnVisibility?: boolean;
  /** Opt-in drill-down (Explore) : la cellule dimension de ce champ affiche un raccourci
   *  « apply filter » au survol (HoverShortcut). Champs supportés ici :
   *  search_term -> filtre Keywords, region_name/country -> Countries,
   *  device_type/device -> Devices, platform -> Platforms.
   *  Omis → comportement inchangé. */

  dimensionFilterField?: string;
  /** Per-field minWidth (KPI field id â†’ px), taken from the Figma maquette. Used as the column's
   *  minWidth floor while flex:1 is kept, so columns still stretch to fill the viewport. Omit a
   *  field â†’ generic default (190 bar / 120 no-bar). */
  columnMinWidths?: Record<string, number>;
  /** Optional KPI definitions supplied by a specific grid. They take precedence over the global KPI catalog. */
  kpiOverrides?: Record<string, any>;
  /** Render Difference as a dedicated sortable column for these metrics, even with inline compare enabled. */
  separateDiffFields?: string[];
};

export default function GenericDataGrid({
  data,
  paginationModel,
  onPaginationModelChange,
  paginationMode,
  rowCount,
  sortModel,
  onSortModelChange,
  sortingMode,
  onFilterModelChange,
  filterModel: filterModelProps,
  filterMode,
  pinnedColumns,
  onExport,
  onVisibilityModelChange,
  visibilityModel: visibilityModelProps,
  apiRef: apiRefProp,
  hideToolbar,
  defaultVisibleFields,
  gridHeight,
  showDimensionFlags,
  metricBarTypes,
  rowHeight,
  columnHeaderHeight,
  leftAlign,
  metricBarColors,
  inlineCompare,
  evolutionMode,
  columnOrder,
  panelAnchorEl,
  columnLabelOverrides,
  brandTrafficMode,
  persistColumnVisibility,
  dimensionFilterField,
  columnMinWidths,
  kpiOverrides,
  separateDiffFields,
  loading,
  disableColumnsLoadingSkeleton,
}: Props) {
  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProp ?? internalApiRef;
  const dispatch = useDispatch();
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const advancedView = filterStore?.isAdvancedView;

  const handleRowClick = (params: any) => {
    const keywords = filterStore?.keywords || [];
    const clickedKeyword = params?.row?.keyword;

    let updatedKeywords;

    if (keywords.includes(clickedKeyword)) {
      updatedKeywords = keywords.filter((keyword: string) => keyword !== clickedKeyword);
    } else {
      updatedKeywords = [...keywords, clickedKeyword]
    }

    dispatch(setFilterKeyword(updatedKeywords));
  };


  // Initialize grid with filters when mounted
  useEffect(() => {
    if ((filterModelProps?.items.length ?? 0) > 0 && apiRef.current) {
      apiRef.current.setFilterModel(filterModelProps as GridFilterModel);
    }
  }, [apiRef]);

  const { isLoading: isKpisLoading } = useKpisQuery();

  const columns = useCreateColumns(data, pinnedColumns, showDimensionFlags, metricBarTypes, leftAlign, metricBarColors, inlineCompare, evolutionMode, columnOrder, columnLabelOverrides, brandTrafficMode, dimensionFilterField, columnMinWidths, kpiOverrides, separateDiffFields);

  // Initial render: keep a single skeleton until both the row data and KPI catalog
  // needed to build the columns are ready. During later refetches, existing rows stay
  // mounted and DataGridPro's native loading overlay is used instead.
  const columnsReady =
    !isKpisLoading &&
    (!data?.length || columns.length > 0);

  const isInitialGridLoading =
    (!data?.length && !!loading) ||
    (!columnsReady && !!data?.length);

  // Initial column visibility. With `defaultVisibleFields`, only those base metrics
  // (plus dimension/pinned columns) start visible; otherwise keep the per-KPI `show` flag.
  const visibleFor = useCallback((column: any) => {
    if (defaultVisibleFields && defaultVisibleFields.length) {
      const isSeparateDiff = separateDiffFields?.some(
        (field) => column.field === `${field}Diff`
      );

      if (isSeparateDiff) {
        return false;
      }

      const base = column.field.replace(/Compare$/, '').replace(/Diff$/, '');
      return (pinnedColumns ?? []).includes(column.field) || defaultVisibleFields.includes(base);
    }
    return column.hide === true ? false : true;
  }, [defaultVisibleFields, pinnedColumns, separateDiffFields]);

  useEffect(() => {
    apiRef.current.forceUpdate?.();
  }, [pinnedColumns, data]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel | null>(null);

  const initialColumnVisibilityModel =
    useMemo(
      () =>
        columns.reduce(
          (acc, column: any) => {
            acc[column.field] =
              advancedView
                ? true
                : visibleFor(column);

            return acc;
          },
          {} as GridColumnVisibilityModel
        ),
      [
        columns,
        advancedView,
        visibleFor,
      ]
    );

  const effectiveColumnVisibilityModel =
    columnVisibilityModel ??
    initialColumnVisibilityModel;

  const handleVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
    onVisibilityModelChange?.(newModel);
  };

  useEffect(() => {
    handleVisibilityChange({
      ...columnVisibilityModel,
      ...columns.reduce((acc, column: any) => {
        acc[column.field] = advancedView ? true : visibleFor(column);
        return acc;
      }, {} as any)
    })
  }, [advancedView])

  // [columns] â€” (rÃ©)initialise la visibilitÃ©. Avec `persistColumnVisibility`, on PRÃ‰SERVE les choix
  // de l'utilisateur (colonnes ajoutÃ©es/retirÃ©es) et on n'attribue le dÃ©faut qu'aux colonnes
  // nouvellement apparues ; sinon comportement historique (reset complet aux dÃ©fauts Ã  chaque
  // reconstruction de `columns`, qui survient sur refetch data ET sur changement d'evolutionMode).
  useEffect(() => {
    const defaults = columns.reduce((acc, column: any) => {
      acc[column.field] = visibleFor(column);
      return acc;
    }, {} as GridColumnVisibilityModel);
    if (persistColumnVisibility && columnVisibilityModel) {
      const merged = { ...columnVisibilityModel };
      let changed = false;
      Object.keys(defaults).forEach((field) => {
        if (!(field in merged)) {
          merged[field] = defaults[field];
          changed = true;
        }
      });
      if (changed) handleVisibilityChange(merged);
    } else {
      handleVisibilityChange(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // [data] â€” un refetch (filtre, tri, paginationâ€¦) ne doit PAS rÃ©initialiser la visibilitÃ© quand
  // la persistance est active. Comportement historique conservÃ© sinon.
  useEffect(() => {
    if (persistColumnVisibility) return;
    handleVisibilityChange({
      ...columnVisibilityModel,
      ...columns.reduce((acc, column: any) => {
        acc[column.field] = advancedView ? true : visibleFor(column);
        return acc;
      }, {} as any)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const Toolbar = (props: any) => {
    return (
      <Stack direction="row" alignItems="center" spacing={0}>
        <GridToolbar {...props} />
        {onExport && (
          <Button startIcon={
            <Iconify icon="lets-icons:export" />
          } sx={{ height: 24 }} color="inherit" onClick={() => onExport(columns)}>
            Export
          </Button>
        )}
      </Stack>
    )
  }

  if (isInitialGridLoading && !disableColumnsLoadingSkeleton) {
    return (
      <Skeleton
        variant="rectangular"
        animation="wave"
        width="100%"
        height={gridHeight || 300}
        sx={{
          borderRadius: 0,
          transform: 'none',
        }}
      />
    );
  }


  return (
    <Box
      sx={{
        width: '100%',
        ...(gridHeight ? { height: gridHeight } : {}),
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },

        // Keep column headers on one line and truncate overflowing text.
        // This also applies while hovering: the header never expands/wraps.
        '& .MuiDataGrid-columnHeaderTitleContainer': {
          minWidth: 0,
          overflow: 'hidden',
        },
        '& .MuiDataGrid-columnHeaderTitleContainerContent': {
          minWidth: 0,
          width: '100%',
          overflow: 'hidden',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },

        '& .MuiDataGrid-cell--pinnedLeft': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? '#212b36 !important'
              : 'white !important',
          zIndex: 3,
        },
        '& .MuiDataGrid-columnHeader--pinnedLeft': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? '#212b36 !important'
              : '#FAFBFB !important',
          zIndex: 4,
        },
        '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? '#2b3642 !important'
              : '#f5f5f5 !important',
          zIndex: 3,
        },
        '& .MuiDataGrid-cell': {
          cursor: 'pointer',
          borderTop: 'none !important',
          borderBottom: '1px solid rgba(145, 158, 171, 0.20) !important',
        },
        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
          color: "#919EAB!important",
        },

        "& .css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input": {
          borderRadius: "6px",
          border: "1px solid rgba(145, 158, 171, 0.20)",
          color: "#919EAB!important",
        },

      }}
    >
      <DataGridPro
        pinnedColumns={{ left: pinnedColumns }}
        {...(rowHeight ? { rowHeight } : {})}
        {...(columnHeaderHeight ? { columnHeaderHeight } : {})}
        autoHeight={!gridHeight}
        loading={loading}
        disableDensitySelector
        checkboxSelection={false}
        disableRowSelectionOnClick
        columnVisibilityModel={
          effectiveColumnVisibilityModel
        }
        onColumnVisibilityModelChange={handleVisibilityChange}
        rows={data}
        pagination
        paginationMode={paginationMode}
        sortingMode={sortingMode ?? 'server'}
        disableMultipleColumnsSorting
        rowCount={rowCount}
        filterMode={filterMode ?? 'server'}
        pageSizeOptions={[10, 20, 50, 100]}
        showCellVerticalBorder={true}
        sortingOrder={['desc', 'asc', null]}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModelProps as GridFilterModel}
        onFilterModelChange={onFilterModelChange}
        columns={columns}
        {...(dimensionFilterField ? {} : { onRowClick: handleRowClick })}
        apiRef={apiRef}
        slots={{
          ...(hideToolbar ? {} : { toolbar: Toolbar }),
          filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
        }}
        slotProps={{
          toolbar: { printOptions: { disableToolbarButton: true }, csvOptions: { disableToolbarButton: !!onExport } },
          filterPanel: {
            logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
          },
          // Anchor the columns/filter panel under the call site's custom header button when provided
          // (Explore). Omitted prop â†’ no `panel` key â†’ existing default anchoring is unchanged.
          ...(panelAnchorEl ? { panel: { anchorEl: panelAnchorEl } } : {}),
        }}
      />
    </Box>
  );
}